import { supabase } from './supabaseClient';
import { getUpstashSensorDataCache } from '../utils/upstashRedisCache';

/**
 * AggregatedDataService - Matches SensorPushWeb implementation for consistent data fetching
 * Fetches aggregated sensor data for charts and latest readings
 */
export class AggregatedDataService {
  /**
   * Fetch aggregated sensor data for charts - much faster and more accurate than raw data sampling
   */
  static async fetchChartData(sensorId, period, startDate, endDate) {
    console.log(`📊 Fetching aggregated data for ${period} period:`, {
      sensorId,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    });

    const cache = getUpstashSensorDataCache();
    const startTimeStr = startDate.toISOString();
    const endTimeStr = endDate.toISOString();

    // ALWAYS try Redis cache first - this is our primary fallback when Supabase is down
    try {
      console.log('🔍 Checking Redis cache for historical data...');
      const cachedData = await cache.getCachedSensorReadings(sensorId, period, startTimeStr, endTimeStr);
      if (cachedData && cachedData.length > 0) {
        console.log(`🎯 Using cached aggregated data for ${period} from Redis`);
        const result = cachedData[0];

        // Validate cached data structure
        if (!result || !result.readings || !Array.isArray(result.readings)) {
          console.log('⚠️ Invalid cached data structure, clearing cache and trying fresh data');
          await cache.clearSensorCache(sensorId);
        } else {
          // Additional staleness check for recent periods (DAY period only, HOUR is handled earlier)
          if (period === 'DAY' && result.readings.length > 0) {
            const timestamps = result.readings.map(r => new Date(r.timestamp).getTime());
            const latestDataTime = new Date(Math.max(...timestamps));
            const dataAge = Date.now() - latestDataTime.getTime();

            // If data is older than 10 minutes for DAY period, consider it stale
            if (dataAge > 10 * 60 * 1000) {
              console.log(`⚠️ Cached data is stale (${Math.round(dataAge/1000)}s old), fetching fresh data`);
              await cache.clearSensorCache(sensorId);
            } else {
              console.log(`✅ Returning ${result.readings.length} fresh cached readings from Redis`);
              return result;
            }
          } else {
            console.log(`✅ Returning ${result.readings.length} cached readings from Redis`);
            return result;
          }
        }
      } else {
        console.log('📭 No cached data found in Redis');
      }
    } catch (cacheError) {
      console.warn('⚠️ Redis cache check failed:', cacheError);
      // Continue to try Supabase - cache failure shouldn't block everything
    }

    // For HOUR period, use minute-level data from raw readings
    if (period === 'HOUR') {
      console.log('🕐 Using minute-level data for HOUR period to show 60-minute progression');
      try {
        return await this.fetchMinuteData(sensorId, startDate, endDate);
      } catch (error) {
        console.error('❌ Failed to fetch minute data for HOUR period:', error);
        // Try to get any cached data as fallback
        return this.getFallbackDataFromCache(sensorId, period, startDate, endDate);
      }
    }

    // Try to fetch from Supabase with better error handling
    try {
      // Calculate days difference for table selection
      const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      
      // Determine which aggregated table to use based on period and date range
      const { tableName, timestampColumn, selectColumns } = this.getTableAndColumns(period, daysDiff);

      console.log(`📋 Using table: ${tableName} with timestamp column: ${timestampColumn}`);

      // Query the appropriate aggregated table
      const { data: aggregatedData, error } = await supabase
        .from(tableName)
        .select(selectColumns)
        .eq('sensor_id', sensorId)
        .gte(timestampColumn, this.formatDateForTable(startDate, tableName))
        .lte(timestampColumn, this.formatDateForTable(endDate, tableName))
        .order(timestampColumn, { ascending: true });

      if (error) {
        console.error('❌ Error fetching aggregated data:', error);
        // Try fallback cache when Supabase fails
        return this.getFallbackDataFromCache(sensorId, period, startDate, endDate);
      }

      if (!aggregatedData || aggregatedData.length === 0) {
        console.log('⚠️ No aggregated data found, trying fallback cache');
        return this.getFallbackDataFromCache(sensorId, period, startDate, endDate);
      }

      console.log(`✅ Retrieved ${aggregatedData.length} aggregated records from ${tableName}`);

      // Check if we have sufficient aggregated data for the time period
      const timeSpanHours = (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60);
      let expectedRecords = 0;

      if (period === 'DAY') {
        expectedRecords = 24; // 24 hours for a day
      } else if (period === 'WEEK') {
        expectedRecords = 7; // 7 days for a week
      } else if (period === 'MONTH') {
        expectedRecords = Math.ceil(timeSpanHours / 24); // Days in the month
      } else if (period === 'YEAR') {
        expectedRecords = 12; // 12 months for a year
      }

      const actualRecords = aggregatedData.length;

      if (expectedRecords > 0) {
        console.log(`📊 Data check: Expected ~${expectedRecords} records, got ${actualRecords}`);

        // If we have less than 80% of expected records, fall back to raw data for better granularity
        if (actualRecords < expectedRecords * 0.8) {
          console.log(`⚠️ Insufficient aggregated data (${actualRecords}/${expectedRecords}), falling back to raw data`);
          return this.fallbackToRawData(sensorId, startDate, endDate);
        }
      }

      // Transform aggregated data to chart format
      const readings = aggregatedData.map((record) => {
        const timestampValue = timestampColumn === 'hour_timestamp' ? record.hour_timestamp :
                              timestampColumn === 'date_timestamp' ? record.date_timestamp :
                              timestampColumn === 'week_start_date' ? record.week_start_date :
                              timestampColumn === 'month_start_date' ? record.month_start_date :
                              record.year_start_date;

        return {
          timestamp: new Date(timestampValue),
          temperature: record.temp_avg,
          humidity: record.humidity_avg,
          batteryLevel: record.battery_avg || undefined
        };
      });

      // Calculate overall stats from aggregated data - use null for missing values, not 0
      const temperatureStats = {
        min: Math.min(...aggregatedData.map((r) => r.temp_min)),
        max: Math.max(...aggregatedData.map((r) => r.temp_max)),
        avg: aggregatedData.reduce((sum, r) => sum + r.temp_avg, 0) / aggregatedData.length,
        first: aggregatedData[0]?.temp_first ?? null,
        last: aggregatedData[aggregatedData.length - 1]?.temp_last ?? null
      };

      const humidityStats = {
        min: Math.min(...aggregatedData.map((r) => r.humidity_min)),
        max: Math.max(...aggregatedData.map((r) => r.humidity_max)),
        avg: aggregatedData.reduce((sum, r) => sum + r.humidity_avg, 0) / aggregatedData.length,
        first: aggregatedData[0]?.humidity_first ?? null,
        last: aggregatedData[aggregatedData.length - 1]?.humidity_last ?? null
      };

      const result = {
        readings,
        temperatureStats,
        humidityStats,
        totalReadings: readings.length
      };

      // Cache the result for future requests with smart TTL
      try {
        // Only cache if we have very recent data
        if (result.readings.length > 0) {
          const timestamps = result.readings.map(r => r.timestamp.getTime());
          const latestDataTime = new Date(Math.max(...timestamps));
          const dataAge = Date.now() - latestDataTime.getTime();

          // Only cache if data is less than 5 minutes old for DAY period (HOUR is handled earlier)
          if (period === 'DAY') {
            if (dataAge < 5 * 60 * 1000) {
              await cache.cacheSensorReadings(sensorId, period, startTimeStr, endTimeStr, [result]);
              console.log(`📦 Cached fresh aggregated data for ${period} period`);
            } else {
              console.log(`⏭️ Skipping cache for stale data (${Math.round(dataAge/1000)}s old)`);
            }
          } else {
            // For longer periods, always cache
            await cache.cacheSensorReadings(sensorId, period, startTimeStr, endTimeStr, [result]);
            console.log(`📦 Cached aggregated data for ${period} period`);
          }
        }
      } catch (cacheError) {
        console.warn('⚠️ Failed to cache aggregated data:', cacheError);
        // Continue without caching - not critical
      }

      return result;

    } catch (error) {
      console.error('❌ Failed to fetch aggregated data from Supabase:', error);
      // Try fallback cache when Supabase fails
      return this.getFallbackDataFromCache(sensorId, period, startDate, endDate);
    }
  }

  // Static cache for minute data to prevent duplicate requests
  static minuteDataCache = new Map();
  static pendingRequests = new Map();
  static requestQueue = [];
  static processingQueue = false;

  /**
   * Batch multiple minute data requests to reduce network load
   */
  static async batchFetchMinuteData(requests) {
    console.log(`📦 Batch fetching minute data for ${requests.length} sensors`);
    
    // Group requests by time range to optimize queries
    const groupedRequests = {};
    requests.forEach(({ sensorId, startDate, endDate }) => {
      const key = `${startDate.toISOString()}-${endDate.toISOString()}`;
      if (!groupedRequests[key]) {
        groupedRequests[key] = [];
      }
      groupedRequests[key].push({ sensorId, startDate, endDate });
    });

    const results = {};
    
    // Process each group
    for (const [timeKey, groupRequests] of Object.entries(groupedRequests)) {
      // Process requests in smaller batches to avoid overwhelming the network
      const batchSize = 3;
      for (let i = 0; i < groupRequests.length; i += batchSize) {
        const batch = groupRequests.slice(i, i + batchSize);
        
        // Process batch concurrently
        const batchPromises = batch.map(({ sensorId, startDate, endDate }) =>
          this.fetchMinuteData(sensorId, startDate, endDate)
            .then(result => ({ sensorId, result }))
            .catch(error => {
              console.error(`❌ Failed to fetch minute data for ${sensorId}:`, error);
              return { 
                sensorId, 
                result: {
                  readings: [],
                  temperatureStats: { min: 0, max: 0, avg: 0, first: 0, last: 0 },
                  humidityStats: { min: 0, max: 0, avg: 0, first: 0, last: 0 },
                  totalReadings: 0
                }
              };
            })
        );
        
        const batchResults = await Promise.all(batchPromises);
        batchResults.forEach(({ sensorId, result }) => {
          results[sensorId] = result;
        });
        
        // Small delay between batches to prevent overwhelming
        if (i + batchSize < groupRequests.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
    }
    
    return results;
  }

  /**
   * Fetch minute-level data for HOUR period with caching and debouncing
   */
  static async fetchMinuteData(sensorId, startDate, endDate) {
    // Create cache key
    const cacheKey = `${sensorId}-${startDate.toISOString()}-${endDate.toISOString()}`;
    
    // Return cached data if available
    if (this.minuteDataCache.has(cacheKey)) {
      console.log('📋 Using cached minute data for', sensorId);
      return this.minuteDataCache.get(cacheKey);
    }

    // Return existing promise if request is pending
    if (this.pendingRequests.has(cacheKey)) {
      console.log('⏳ Waiting for pending minute data request for', sensorId);
      return this.pendingRequests.get(cacheKey);
    }

    console.log('🕐 Fetching minute-level data for HOUR period');

    // Create and cache the request promise
    const requestPromise = this._fetchMinuteDataInternal(sensorId, startDate, endDate);
    this.pendingRequests.set(cacheKey, requestPromise);

    try {
      const result = await requestPromise;
      
      // Cache the result for 30 seconds
      this.minuteDataCache.set(cacheKey, result);
      setTimeout(() => {
        this.minuteDataCache.delete(cacheKey);
      }, 30000);
      
      return result;
    } finally {
      // Clean up pending request
      this.pendingRequests.delete(cacheKey);
    }
  }

  /**
   * Internal method to actually fetch minute data
   */
  static async _fetchMinuteDataInternal(sensorId, startDate, endDate) {
    try {
      // Query raw sensor readings for the past hour with retry logic
      let retryCount = 0;
      const maxRetries = 2;
      
      while (retryCount <= maxRetries) {
        try {
          const { data: rawData, error } = await supabase
            .from('sensor_readings')
            .select('temperature, humidity, battery_level, recorded_at')
            .eq('sensor_id', sensorId)
            .gte('recorded_at', startDate.toISOString())
            .lte('recorded_at', endDate.toISOString())
            .order('recorded_at', { ascending: true });

          if (error) {
            throw error;
          }

          if (!rawData || rawData.length === 0) {
            console.log('⚠️ No minute data found');
            return {
              readings: [],
              temperatureStats: { min: 0, max: 0, avg: 0, first: 0, last: 0 },
              humidityStats: { min: 0, max: 0, avg: 0, first: 0, last: 0 },
              totalReadings: 0
            };
          }

          console.log(`✅ Retrieved ${rawData.length} minute-level readings`);

          // Check if we have recent data (within last 15 minutes)
          const now = Date.now();
          const recentDataThreshold = 15 * 60 * 1000; // 15 minutes
          const hasRecentData = rawData.some(record =>
            now - new Date(record.recorded_at).getTime() < recentDataThreshold
          );

          if (!hasRecentData && rawData.length > 0) {
            const latestRecord = rawData[rawData.length - 1];
            const dataAge = Math.round((now - new Date(latestRecord.recorded_at).getTime()) / 1000 / 60);
            console.log(`⚠️ No recent data found. Latest reading is ${dataAge} minutes old. This device may be offline.`);
          }

          // Use ONLY actual raw data - NO interpolation or synthetic data generation
          const readings = rawData
            .filter(record => record.temperature != null && record.temperature !== undefined && !isNaN(record.temperature))
            .map(record => ({
              timestamp: new Date(record.recorded_at),
              temperature: record.temperature,
              humidity: record.humidity,
              batteryLevel: record.battery_level
            }));

          console.log(`📊 Using ${readings.length} actual raw data points (no interpolation)`);

          const temperatures = readings.map(r => r.temperature);
          const humidities = readings.map(r => r.humidity);

          const temperatureStats = temperatures.length > 0 ? {
            min: Math.min(...temperatures),
            max: Math.max(...temperatures),
            avg: temperatures.reduce((sum, temp) => sum + temp, 0) / temperatures.length,
            first: temperatures[0],
            last: temperatures[temperatures.length - 1]
          } : { min: 0, max: 0, avg: 0, first: 0, last: 0 };

          const humidityStats = humidities.length > 0 ? {
            min: Math.min(...humidities),
            max: Math.max(...humidities),
            avg: humidities.reduce((sum, hum) => sum + hum, 0) / humidities.length,
            first: humidities[0],
            last: humidities[humidities.length - 1]
          } : { min: 0, max: 0, avg: 0, first: 0, last: 0 };

          const result = {
            readings,
            temperatureStats,
            humidityStats,
            totalReadings: readings.length
          };

          console.log(`✅ Minute data result: ${result.readings.length} readings`);
          return result;

        } catch (fetchError) {
          retryCount++;
          
          if (fetchError.message && fetchError.message.includes('ERR_INSUFFICIENT_RESOURCES')) {
            console.warn(`⚠️ Network overload detected (attempt ${retryCount}/${maxRetries + 1}), retrying...`);
            
            if (retryCount <= maxRetries) {
              // Exponential backoff
              const delay = Math.pow(2, retryCount) * 1000;
              await new Promise(resolve => setTimeout(resolve, delay));
              continue;
            }
          }
          
          throw fetchError;
        }
      }
      
      // This should not be reached, but just in case
      throw new Error('Max retries exceeded');
      
    } catch (error) {
      console.error('❌ Error fetching minute data:', error);
      
      // Return empty response when network is overloaded
      if (error.message && error.message.includes('ERR_INSUFFICIENT_RESOURCES')) {
        console.log('🔄 Network overloaded, returning empty chart data');
      }
      
      return {
        readings: [],
        temperatureStats: { min: 0, max: 0, avg: 0, first: 0, last: 0 },
        humidityStats: { min: 0, max: 0, avg: 0, first: 0, last: 0 },
        totalReadings: 0
      };
    }
  }

  /**
   * Determine the correct table and columns to query based on the period
   */
  static getTableAndColumns(period, daysDiff) {
    if (period === 'HOUR' || daysDiff <= 1) {
      return {
        tableName: 'sensor_readings_hourly',
        timestampColumn: 'hour_timestamp',
        selectColumns: 'hour_timestamp, temp_min, temp_max, temp_avg, temp_first, temp_last, humidity_min, humidity_max, humidity_avg, humidity_first, humidity_last, battery_avg, reading_count'
      };
    } else if (period === 'WEEK' || daysDiff <= 30) {
      return {
        tableName: 'sensor_readings_daily',
        timestampColumn: 'date_timestamp',
        selectColumns: 'date_timestamp, temp_min, temp_max, temp_avg, temp_first, temp_last, humidity_min, humidity_max, humidity_avg, humidity_first, humidity_last, battery_avg, reading_count'
      };
    } else if (period === 'MONTH' || daysDiff <= 365) {
      return {
        tableName: 'sensor_readings_weekly',
        timestampColumn: 'week_start_date',
        selectColumns: 'week_start_date, temp_min, temp_max, temp_avg, temp_first, temp_last, humidity_min, humidity_max, humidity_avg, humidity_first, humidity_last, battery_avg, reading_count'
      };
    } else {
      return {
        tableName: 'sensor_readings_monthly',
        timestampColumn: 'month_start_date',
        selectColumns: 'month_start_date, temp_min, temp_max, temp_avg, temp_first, temp_last, humidity_min, humidity_max, humidity_avg, humidity_first, humidity_last, battery_avg, reading_count'
      };
    }
  }

  /**
   * Format date appropriately for the table type
   */
  static formatDateForTable(date, tableName) {
    if (tableName === 'sensor_readings_daily' ||
        tableName === 'sensor_readings_weekly' ||
        tableName === 'sensor_readings_monthly' ||
        tableName === 'sensor_readings_yearly') {
      // Date columns need date format
      return date.toISOString().split('T')[0];
    } else {
      // Timestamp columns need full ISO format
      return date.toISOString();
    }
  }

  /**
   * Get fallback data from cache when Supabase is down
   */
  static async getFallbackDataFromCache(sensorId, _period, startDate, endDate) {
    console.log('🔄 Trying to get fallback data from cache...');

    const cache = getUpstashSensorDataCache();

    try {
      // Try to get any cached data for this sensor, regardless of period
      const cachedData = await cache.getCachedSensorReadings(sensorId, 'DAY', startDate.toISOString(), endDate.toISOString());
      if (cachedData && cachedData.length > 0) {
        console.log('🎯 Found fallback cached data');
        const result = cachedData[0];
        if (result && result.readings && Array.isArray(result.readings)) {
          return result;
        }
      }
    } catch (error) {
      console.warn('⚠️ Fallback cache access failed:', error);
    }

    // If no cached data available, return empty response
    console.log('📭 No fallback data available - returning empty chart');
    return {
      readings: [],
      temperatureStats: { min: 0, max: 0, avg: 0, first: 0, last: 0 },
      humidityStats: { min: 0, max: 0, avg: 0, first: 0, last: 0 },
      totalReadings: 0
    };
  }

  /**
   * Fallback to raw data if aggregated data is not available
   */
  static async fallbackToRawData(sensorId, startDate, endDate) {
    console.log('🔄 Falling back to raw data query');

    try {
      const { data: rawData, error } = await supabase
        .from('sensor_readings')
        .select('temperature, humidity, battery_level, recorded_at')
        .eq('sensor_id', sensorId)
        .gte('recorded_at', startDate.toISOString())
        .lte('recorded_at', endDate.toISOString())
        .order('recorded_at', { ascending: true })
        .limit(5000); // Reasonable limit for fallback

      if (error) {
        console.error('❌ Error in raw data fallback:', error);
        // Return empty response when Supabase is completely down
        return {
          readings: [],
          temperatureStats: { min: 0, max: 0, avg: 0, first: 0, last: 0 },
          humidityStats: { min: 0, max: 0, avg: 0, first: 0, last: 0 },
          totalReadings: 0
        };
      }

      if (!rawData || rawData.length === 0) {
        console.log('⚠️ No raw data found');
        return {
          readings: [],
          temperatureStats: { min: 0, max: 0, avg: 0, first: 0, last: 0 },
          humidityStats: { min: 0, max: 0, avg: 0, first: 0, last: 0 },
          totalReadings: 0
        };
      }

      console.log(`📊 Raw data fallback: ${rawData.length} readings`);

      // Use ONLY actual raw data - NO interpolation or synthetic data generation
      const readings = rawData
        .filter(record => record.temperature != null && record.temperature !== undefined && !isNaN(record.temperature))
        .map(record => ({
          timestamp: new Date(record.recorded_at),
          temperature: record.temperature,
          humidity: record.humidity,
          batteryLevel: record.battery_level
        }));

      console.log(`📊 Using ${readings.length} actual raw data points (no interpolation)`);

      const temperatures = readings.map(r => r.temperature);
      const humidities = readings.map(r => r.humidity);

      const temperatureStats = temperatures.length > 0 ? {
        min: Math.min(...temperatures),
        max: Math.max(...temperatures),
        avg: temperatures.reduce((sum, temp) => sum + temp, 0) / temperatures.length,
        first: temperatures[0],
        last: temperatures[temperatures.length - 1]
      } : { min: 0, max: 0, avg: 0, first: 0, last: 0 };

      const humidityStats = humidities.length > 0 ? {
        min: Math.min(...humidities),
        max: Math.max(...humidities),
        avg: humidities.reduce((sum, hum) => sum + hum, 0) / humidities.length,
        first: humidities[0],
        last: humidities[humidities.length - 1]
      } : { min: 0, max: 0, avg: 0, first: 0, last: 0 };

      const result = {
        readings,
        temperatureStats,
        humidityStats,
        totalReadings: rawData.length
      };

      return result;

    } catch (error) {
      console.error('❌ Error in fallbackToRawData:', error);
      // Return empty result instead of throwing
      return {
        readings: [],
        temperatureStats: { min: 0, max: 0, avg: 0, first: 0, last: 0 },
        humidityStats: { min: 0, max: 0, avg: 0, first: 0, last: 0 },
        totalReadings: 0
      };
    }
  }
}

export default AggregatedDataService;
